import React, {
  useCallback,
  useContext,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  ImageRegular,
  TextFontRegular,
} from '@fluentui/react-icons';
import { ColorPicker } from '@bingads-webui-campaign-react/ads-color-picker';
import { EntryCardSVGIcons } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { actionTypes } from './reducer';
import { BrandKitContext } from './context';
import { ContentPanel } from './content-panel';
import { useBrandKitPageStyle } from './styles';
import { BrandKitImageListElement, BrandKitFontsSelector } from './components';

const MAX_SQUARE_LOGOS = 5;
const MAX_LANDSCAPE_LOGOS = 5;
const MAX_IMAGES = 15;
export const BrandElements = ({
  i18n,
  preferenceService,
  serverContext,
  aid,
  loading,
  cid,
  oDataConfig,
  fetchBrandKitData,
  perfMarker,
  permissions,
  brandKitDataService,
}) => {
  const { state, dispatch } = useContext(BrandKitContext);
  const classes = useBrandKitPageStyle();
  const {
    id,
    brandkitName,
    fonts = [],
    images = [],
    logos = [],
    palettes = [],
    squareLogosCount,
    landscapeLogosCount,
  } = state || {};

  // eslint-disable-next-line no-unused-vars
  const updateOrCreateBrandKit = useCallback(async (brandKit) => {
    if (!id) {
      const { error } = await brandKitDataService.createBrandKits(aid, [{
        brandkitName: 'Brandkit',
        ...brandKit,
      },
      ]);
      if (error) {
        // throw new error('Error while creating brandkit');
        return;
      }
      fetchBrandKitData();
      return;
    }
    const { error } = await brandKitDataService.updateBrandKit(aid, [{
      id,
      brandkitName,
      ...brandKit,
    }]);
    if (error) {
      // throw new error('Error while updating brandkit');
      return;
    }
    fetchBrandKitData();
  }, [id, brandKitDataService, fonts, brandkitName, images, logos, palettes, fetchBrandKitData, aid]);


  const onUpdateColor = useCallback((colorId, colorObj) => {
    if (colorId === '') {
      // ADD
      palettes.push({ Name: colorObj.str, HexCode: colorObj.str, Type: '' });
      updateOrCreateBrandKit({ palettes });
      return;
    }
    // EDIT
    const updatePalette = palettes.map((palette) => {
      if (palette.Name === colorId) {
        return { Name: colorObj.str, HexCode: colorObj.str, Type: '' };
      }
      return palette;
    });
    updateOrCreateBrandKit({ palettes: updatePalette });
  }, [palettes, updateOrCreateBrandKit]);
  const onDeleteColor = useCallback((ev) => {
    const { colorid } = ev.currentTarget.dataset;
    if (colorid === '') {
      return;
    }
    const updatePalette = palettes.filter(palette => palette.Name !== colorid);
    updateOrCreateBrandKit({ palettes: updatePalette });
  }, [palettes, updateOrCreateBrandKit]);

  const onUpdateFonts = useCallback((updatedFonts) => {
    dispatch({
      type: actionTypes.updateFonts,
      payload: updatedFonts,
    });
    updateOrCreateBrandKit({ fonts: updatedFonts });
  }, [dispatch, updateOrCreateBrandKit]);

  const totalLogosCount = MAX_LANDSCAPE_LOGOS + MAX_SQUARE_LOGOS;
  const maxLogosLimit = logos ? totalLogosCount - logos.length : totalLogosCount;
  const maxImagesLimit = images ? MAX_IMAGES - images.length : MAX_IMAGES;
  const maxSquareLogosLimit = squareLogosCount ? MAX_SQUARE_LOGOS - squareLogosCount : MAX_SQUARE_LOGOS;
  const maxLandscapeLogosLimit = landscapeLogosCount ? MAX_LANDSCAPE_LOGOS - landscapeLogosCount : MAX_LANDSCAPE_LOGOS;

  return (
    <div
      className={classNames('ads-studio-brand-elements', classes.brandElements)}
    >
      <BrandKitImageListElement
        isLoading={loading}
        elementType="logo"
        panelTitle={_TL_('Add logos for your brand')}
        contentBefore={<img src={EntryCardSVGIcons.BrandKitLogoIconSVG} alt={i18n.getString('Logos')} />}
        title={i18n.getString(_TL_('Logos'))}
        panelSubtitle={_TL_("You can create assets, choose assets you've already uploaded to Ads Studio, or browse to add a new file.")}
        subTitle={i18n.getString(_TL_('You can add up to five logos with a 1:1 aspect ratio and five logos with a 4:1 aspect ratio.'))}
        maxLimit={maxLogosLimit}
        maxSquareLogosLimit={maxSquareLogosLimit}
        maxLandscapeLogosLimit={maxLandscapeLogosLimit}
        data={logos}
        i18n={i18n}
        maxCount={10}
        preferenceService={preferenceService}
        permissions={permissions}
        serverContext={serverContext}
        aid={aid}
        cid={cid}
        updateOrCreateBrandKit={updateOrCreateBrandKit}
        oDataConfig={oDataConfig}
        perfMarker={perfMarker}
        helpTopic="pop_BA_Brand_Logos"
        helpLabel={i18n.getString(_TL_('Learn more about Logos'))}
      />
      <ColorPicker
        maxColors={5}
        onUpdate={onUpdateColor}
        onDelete={onDeleteColor}
        isLoading={loading}
        i18n={i18n}
        paletteData={palettes}
        title={i18n.getString(_TL_('Colors'))}
        subTitle={i18n.getString(_TL_("Define your brand's color palette for a consistent, recognizable visual identity."))}
        helpLabel={i18n.getString(_TL_('Learn more about Brand Colors'))}
        helpTopic="pop_BA_Brand_Colors"
      />
      <BrandKitImageListElement
        isLoading={loading}
        elementType="image"
        panelTitle={_TL_('Add images for your brand ')}
        panelSubtitle={_TL_("You can create assets, choose assets you've already uploaded to Ads Studio or browse to add a new file.")}
        contentBefore={<ImageRegular />}
        subTitle={i18n.getString(_TL_('Add up to 15 images associated with your brand.'))}
        maxLimit={maxImagesLimit}
        title={i18n.getString(_TL_('Images'))}
        data={images}
        i18n={i18n}
        maxCount={15}
        preferenceService={preferenceService}
        permissions={permissions}
        serverContext={serverContext}
        aid={aid}
        updateOrCreateBrandKit={updateOrCreateBrandKit}
        showEditBtn={false}
        cid={cid}
        oDataConfig={oDataConfig}
        perfMarker={perfMarker}
        helpTopic="pop_BA_Brand_Images"
        helpLabel={i18n.getString(_TL_('Learn more about Images'))}
      />
      <ContentPanel
        title={i18n.getString(_TL_('Fonts'))}
        isLoading={false}
        headerProps={{
          contentBefore: <TextFontRegular />,
        }}
        helpTopic="pop_BA_Brand_Font"
        helpLabel={i18n.getString(_TL_('Learn more about Fonts'))}
        bodyContent={
          <BrandKitFontsSelector i18n={i18n} fonts={fonts} onChangeFonts={onUpdateFonts} />
        }
      />
    </div>
  );
};

BrandElements.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  fetchBrandKitData: PropTypes.func.isRequired,
  brandKitDataService: PropTypes.shape({
    getBrandKits: PropTypes.func,
    createBrandKits: PropTypes.func,
    updateBrandKit: PropTypes.func,
  }).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}),
};

BrandElements.defaultProps = {
  serverContext: {},
};
